import { Button } from "@dashboard/components/Button";
import PageHeader from "@dashboard/components/PageHeader";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { TextField } from "@material-ui/core";
import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { deleteCmsPage, listCmsPages } from "../api";
import { CmsPage } from "../types";
import { cmsPageAddUrl, cmsPageUrl } from "../urls";

const statusLabel = (status: CmsPage["status"]) => (status === "published" ? "Published" : "Draft");

const CmsPagesList = () => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const [pages, setPages] = React.useState<CmsPage[]>([]);
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const loadPages = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await listCmsPages({ search });

      setPages(data.docs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load CMS pages");
    } finally {
      setLoading(false);
    }
  }, [search]);

  React.useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleDelete = async (page: CmsPage) => {
    const confirmed = window.confirm(`Delete "${page.title}"?`);

    if (!confirmed) {
      return;
    }

    try {
      await deleteCmsPage(page.id);
      notify({ status: "success", text: "CMS page deleted" });
      loadPages();
    } catch (err) {
      notify({
        status: "error",
        text: err instanceof Error ? err.message : "Unable to delete CMS page",
      });
    }
  };

  return (
    <>
      <Box paddingX={6} paddingTop={6}>
        <PageHeader title="CMS Pages">
          <Button variant="primary" onClick={() => navigate(cmsPageAddUrl())}>
            Create CMS Page
          </Button>
        </PageHeader>
      </Box>
      <Box paddingX={6} paddingBottom={6}>
        <Box marginBottom={4} style={{ maxWidth: 480 }}>
          <TextField
            fullWidth
            label="Search pages"
            value={search}
            onChange={event => setSearch(event.target.value)}
            variant="outlined"
          />
        </Box>
        {loading ? (
          <Box padding={8} textAlign="center">
            <Text>Loading CMS pages...</Text>
          </Box>
        ) : error ? (
          <Box padding={8} textAlign="center">
            <Text color="critical1">{error}</Text>
          </Box>
        ) : pages.length === 0 ? (
          <Box padding={10} textAlign="center">
            <Text size={4}>No CMS pages found.</Text>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3}>
            {pages.map(page => (
              <Box
                key={page.id}
                borderColor="default1"
                borderRadius={3}
                borderStyle="solid"
                borderWidth={1}
                padding={5}
                backgroundColor="default1"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    style={{ minWidth: 0, flex: 1 }}
                  >
                    <Text size={4} fontWeight="bold">
                      {page.title}
                    </Text>
                    <Text size={2} color="default2">
                      /{page.slug} · {page.pageType} · {statusLabel(page.status)} · Updated{" "}
                      {new Date(page.updatedAt).toLocaleDateString()}
                    </Text>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Button variant="secondary" onClick={() => navigate(cmsPageUrl(page.id))}>
                      Edit
                    </Button>
                    <Button variant="secondary" onClick={() => handleDelete(page)}>
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default CmsPagesList;
